<template>
  <div>
    <!-- Page Header -->
    <CCard class="mb-4 border-0">
      <CCardBody class="p-4">
        <CRow class="align-items-center">
          <CCol md="6">
            <h1 class="h4 mb-1 font-weight-bold">Vendor quotations</h1>
            <p class="text-muted mb-0">
              Keep the quotations you receive from vendors in one place
            </p>
          </CCol>
          <CCol md="6" class="d-flex justify-content-md-end mt-3 mt-md-0">
            <CButton
              color="light"
              class="mr-2"
              aria-label="Refresh"
              title="Refresh"
              @click="refreshAll"
            >
              <CIcon name="cil-reload" />
            </CButton>
            <CButton color="primary" @click="addNew">
              <CIcon name="cil-plus" /> Record vendor quotation
            </CButton>
          </CCol>
        </CRow>
      </CCardBody>
    </CCard>

    <!-- Vendor Quotation Insights -->
    <CCard class="mb-4 border-0">
      <CCardHeader class="bg-transparent border-0 p-4">
        <h2 class="h6 font-weight-bold mb-0">Vendor quotation summary</h2>
        <div class="small text-muted">All vendor quotations · trend for the last 6 months</div>
      </CCardHeader>
      <CCardBody class="pt-0 pb-4 px-4">
        <CRow class="mb-4">
          <CCol sm="6" md="4" class="mb-3 mb-md-0">
            <div class="inv-kpi">
              <div class="inv-kpi-top">
                <span class="inv-kpi-label">Total quotations</span>
                <span class="inv-kpi-chip chip-navy"><CIcon name="cil-file" /></span>
              </div>
              <div class="inv-kpi-value">{{ quotationSummary.totalCount }}</div>
              <div class="inv-kpi-sub">All time</div>
            </div>
          </CCol>
          <CCol sm="6" md="4" class="mb-3 mb-md-0">
            <div class="inv-kpi">
              <div class="inv-kpi-top">
                <span class="inv-kpi-label">This month</span>
                <span class="inv-kpi-chip chip-navy"><CIcon name="cil-calendar" /></span>
              </div>
              <div class="inv-kpi-value">{{ quotationSummary.thisMonthCount }}</div>
              <div class="inv-kpi-sub" :class="quotationSummary.countChange >= 0 ? 'trend-up' : 'trend-down'">
                <CIcon :name="quotationSummary.countChange >= 0 ? 'cil-arrow-top' : 'cil-arrow-bottom'" />
                {{ Math.abs(quotationSummary.countChange) }}% vs last month
              </div>
            </div>
          </CCol>
          <CCol sm="12" md="4">
            <div class="inv-kpi">
              <div class="inv-kpi-top">
                <span class="inv-kpi-label">Total quoted</span>
                <span class="inv-kpi-chip chip-success"><CIcon name="cil-calculator" /></span>
              </div>
              <div class="inv-kpi-value">{{ formatCurrency(quotationSummary.totalAmount) }}</div>
              <div class="inv-kpi-sub">All time</div>
            </div>
          </CCol>
        </CRow>
        <PurchaseQuotationTrendChart
          :chartData="quotationTrendData"
          style="height: 250px"
        />
      </CCardBody>
    </CCard>

    <!-- Vendor Quotation List -->
    <CCard class="mb-4 border-0">
      <CCardHeader class="bg-transparent border-0 p-4">
        <h2 class="h6 font-weight-bold mb-0">
          Vendor quotation list
          <CBadge color="light" class="ml-2">
            {{ hasActiveFilters ? `${computedItems.length} of ${items.length}` : items.length }}
          </CBadge>
        </h2>
      </CCardHeader>
      <CCardBody class="p-4">
        <!-- Filters -->
        <CRow class="mb-3">
          <CCol>
            <CButton color="light" @click="showFilters = !showFilters">
              <CIcon :name="showFilters ? 'cil-chevron-top' : 'cil-filter'" />
              {{ showFilters ? "Hide filters" : "Show filters" }}
            </CButton>
            <CBadge v-if="hasActiveFilters" color="primary" class="ml-2">
              {{ activeFiltersCount }} active
            </CBadge>
            <CButton v-if="hasActiveFilters" color="link" class="ml-1" @click="resetFilters">
              Clear filters
            </CButton>
          </CCol>
        </CRow>
        <CCollapse :show="showFilters">
          <CCard class="mb-4 border">
            <CCardBody>
              <CRow>
                <CCol md="3">
                  <CInput
                    label="Quotation reference"
                    v-model="filters.orderNo"
                    placeholder="Search reference"
                  />
                </CCol>
                <CCol md="3">
                  <CInput
                    label="Vendor"
                    v-model="filters.supplierName"
                    placeholder="Search vendor"
                  />
                </CCol>
                <CCol md="3">
                  <CInput label="From" type="date" v-model="filters.dateFrom" />
                </CCol>
                <CCol md="3">
                  <CInput
                    label="To"
                    type="date"
                    v-model="filters.dateTo"
                    :is-valid="dateRangeValid ? null : false"
                    invalid-feedback="Choose a date on or after the From date"
                  />
                </CCol>
              </CRow>
            </CCardBody>
          </CCard>
        </CCollapse>

        <!-- Loading -->
        <div v-if="loading && !items.length" class="text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="sr-only">Loading vendor quotations</span>
          </div>
          <p class="mt-3 text-muted">Loading vendor quotations…</p>
        </div>

        <!-- Error -->
        <div v-else-if="loadError" class="text-center py-5">
          <CIcon name="cil-warning" size="2xl" class="mb-3 text-danger" />
          <h3 class="h6 font-weight-bold">Couldn't load vendor quotations</h3>
          <p class="text-muted mb-3">Check your connection and try again.</p>
          <CButton color="primary" @click="refreshAll">
            <CIcon name="cil-reload" /> Try again
          </CButton>
        </div>

        <!-- Empty (first use) -->
        <div v-else-if="!items.length" class="text-center py-5">
          <CIcon name="cil-file" size="2xl" class="mb-3 text-muted" />
          <h3 class="h6 font-weight-bold">No vendor quotations yet</h3>
          <p class="text-muted mb-3">Record a quotation you received from a vendor so you can compare prices later.</p>
          <CButton color="primary" @click="addNew">
            <CIcon name="cil-plus" /> Record vendor quotation
          </CButton>
        </div>

        <!-- Empty (filtered) -->
        <div v-else-if="!computedItems.length" class="text-center py-5">
          <CIcon name="cil-filter" size="2xl" class="mb-3 text-muted" />
          <h3 class="h6 font-weight-bold">No vendor quotations match your filters</h3>
          <CButton color="light" class="mt-2" @click="resetFilters">
            <CIcon name="cil-x" /> Clear filters
          </CButton>
        </div>

        <template v-else>
          <div v-if="loading" class="progress mb-2" style="height: 3px">
            <div class="progress-bar progress-bar-striped progress-bar-animated w-100" role="progressbar" aria-label="Refreshing"></div>
          </div>

          <!-- Desktop table -->
          <div class="d-none d-lg-block table-responsive">
            <table class="table table-hover border">
              <thead>
                <tr>
                  <th style="width: 50px">#</th>
                  <th>Reference</th>
                  <th>Vendor</th>
                  <th>Date</th>
                  <th class="text-right">Amount</th>
                  <th class="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in computedItems" :key="item.id">
                  <td class="text-muted">{{ index + 1 }}</td>
                  <td>
                    <router-link
                      :to="`/tenants/PurchaseQuotation/${item.id}`"
                      class="font-weight-bold text-monospace"
                    >{{ item.orderNo || "—" }}</router-link>
                    <CBadge
                      v-if="item.status"
                      :color="badgeColor('purchaseQuotation', item.status)"
                      class="ml-2"
                    >
                      {{ badgeLabel("purchaseQuotation", item.status) }}
                    </CBadge>
                  </td>
                  <td>{{ item.supplierName }}</td>
                  <td>{{ item.displayDate }}</td>
                  <td class="text-right font-weight-bold">
                    {{ formatCurrency(item.totalAmount) }}
                  </td>
                  <td class="text-right">
                    <CButtonGroup>
                      <CButton
                        color="light"
                        size="sm"
                        class="border"
                        :aria-label="`Edit ${item.orderNo}`"
                        title="Edit"
                        @click="onEdit(item)"
                      >
                        <CIcon name="cil-pencil" />
                      </CButton>
                      <CButton
                        color="light"
                        size="sm"
                        class="border"
                        :aria-label="`Delete ${item.orderNo}`"
                        title="Delete"
                        @click="showDeleteConfirmation(item)"
                      >
                        <CIcon name="cil-trash" />
                      </CButton>
                    </CButtonGroup>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Mobile card list -->
          <div class="d-lg-none">
            <CCard v-for="item in computedItems" :key="item.id" class="mb-3 border">
              <CCardBody class="p-3">
                <div class="d-flex justify-content-between align-items-start mb-2">
                  <router-link
                    :to="`/tenants/PurchaseQuotation/${item.id}`"
                    class="font-weight-bold text-monospace"
                  >{{ item.orderNo || "—" }}</router-link>
                  <CBadge v-if="item.status" :color="badgeColor('purchaseQuotation', item.status)">
                    {{ badgeLabel("purchaseQuotation", item.status) }}
                  </CBadge>
                </div>
                <div class="d-flex justify-content-between mb-1">
                  <span class="text-muted small">Vendor</span>
                  <span>{{ item.supplierName }}</span>
                </div>
                <div class="d-flex justify-content-between mb-1">
                  <span class="text-muted small">Date</span>
                  <span>{{ item.displayDate }}</span>
                </div>
                <div class="d-flex justify-content-between mb-2">
                  <span class="text-muted small">Amount</span>
                  <span class="font-weight-bold">{{ formatCurrency(item.totalAmount) }}</span>
                </div>
                <div class="d-flex justify-content-between">
                  <CButton color="light" @click="onEdit(item)">
                    <CIcon name="cil-pencil" /> Edit
                  </CButton>
                  <CButton
                    color="light"
                    class="text-danger"
                    :aria-label="`Delete ${item.orderNo}`"
                    @click="showDeleteConfirmation(item)"
                  >
                    <CIcon name="cil-trash" /> Delete
                  </CButton>
                </div>
              </CCardBody>
            </CCard>
          </div>
        </template>
      </CCardBody>
    </CCard>

    <!-- Delete Confirmation Modal -->
    <CModal
      :title="`Delete vendor quotation ${itemToDelete.orderNo || ''}?`"
      :show.sync="warningModal"
      :close-on-backdrop="!deleting"
      @update:show="onDeleteConfirmation"
    >
      <p class="mb-0">
        Vendor quotation <strong>{{ itemToDelete.orderNo }}</strong>
        <span v-if="itemToDelete.supplierName && itemToDelete.supplierName !== '—'"> from {{ itemToDelete.supplierName }}</span>
        and its attached document link will be removed. This can't be undone.
      </p>
      <template #footer>
        <CButton color="light" :disabled="deleting" @click="warningModal = false">Keep it</CButton>
        <CButton color="danger" :disabled="deleting" @click="confirmDelete">
          {{ deleting ? "Deleting…" : "Delete quotation" }}
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import PurchaseQuotationApi from "@/lib/purchaseQuotationApi";
import PurchaseQuotationTrendChart from "../widgets/PurchaseQuotationTrendChart";
import { formatMyr } from "@/utils/money";
import { formatDate, asDateString, mytToday } from "@/utils/datetime";
import { badgeColor, badgeLabel, badgeChartColor } from "@/utils/statusBadges";

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** Last `n` calendar months (oldest first) as "yyyy-MM" keys, in Malaysia time. */
function lastMonthKeys(n) {
  const [y, m] = mytToday().split("-").map(Number);
  const keys = [];
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(Date.UTC(y, m - 1 - i, 1));
    keys.push(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`);
  }
  return keys;
}

export default {
  name: "PurchaseQuotationList",
  components: {
    PurchaseQuotationTrendChart,
  },
  data() {
    return {
      loading: true,
      loadError: false,
      deleting: false,
      items: [],
      api: new PurchaseQuotationApi(),
      warningModal: false,
      itemToDelete: {},

      // Filters (client-side, applied instantly)
      showFilters: false,
      filters: {
        orderNo: "",
        supplierName: "",
        dateFrom: "",
        dateTo: "",
      },

      // Real analytics source — all vendor quotations for org-wide summary
      allPurchaseQuotations: [],
    };
  },
  mounted() {
    this.$store.commit("setPageNavItems", ["Expenses", "Purchase quotations"]);
    this.refreshTable();
    this.fetchSummary(); // Load org-wide vendor quotations for real analytics
  },
  computed: {
    computedItems() {
      return this.items
        .map((item) => ({
          ...item,
          displayDate: formatDate(item.date) || "—",
          supplierName: this.getSupplierName(item),
          totalAmount: this.getTotalAmount(item),
        }))
        .filter((item) => this.matchesFilters(item));
    },

    // Filter tracking
    hasActiveFilters() {
      return Object.values(this.filters).some((filter) => filter !== "");
    },
    activeFiltersCount() {
      return Object.values(this.filters).filter((filter) => filter !== "").length;
    },
    dateRangeValid() {
      const f = this.filters;
      return !f.dateFrom || !f.dateTo || f.dateTo >= f.dateFrom;
    },

    // ===== Real analytics (computed from allPurchaseQuotations) =====
    quotationSummary() {
      const all = this.allPurchaseQuotations || [];
      const totalAmount = all.reduce((a, q) => a + this.getTotalAmount(q), 0);
      const [lastKey, thisKey] = lastMonthKeys(2);
      const inMonth = (key) => all.filter((q) => this.monthKey(q.date) === key).length;
      const cThis = inMonth(thisKey), cLast = inMonth(lastKey);
      const countChange = cLast ? Math.round(((cThis - cLast) / cLast) * 1000) / 10 : (cThis > 0 ? 100 : 0);
      return { totalCount: all.length, thisMonthCount: cThis, countChange, totalAmount };
    },

    // Chart data for vendor quotation trends — last 6 months quoted value (real)
    quotationTrendData() {
      const months = lastMonthKeys(6);
      const all = this.allPurchaseQuotations || [];
      const totals = months.map((key) =>
        all.filter((q) => this.monthKey(q.date) === key).reduce((a, q) => a + this.getTotalAmount(q), 0)
      );
      const primary = badgeChartColor("invoice", "Approve"); // theme primary
      return {
        labels: months.map((key) => MONTH_NAMES[Number(key.slice(5, 7)) - 1]),
        datasets: [
          { label: "Quoted", backgroundColor: "transparent", borderColor: primary, pointBackgroundColor: primary, borderWidth: 2, fill: false, data: totals },
        ],
      };
    },
  },
  methods: {
    badgeColor,
    badgeLabel,

    monthKey(value) {
      return asDateString(value).slice(0, 7);
    },

    getSupplierName(item) {
      if (item.vendor && item.vendor.name) return item.vendor.name;
      if (item.supplier && item.supplier.name) return item.supplier.name;
      return "—";
    },

    getTotalAmount(quotation) {
      var total = 0;
      if (quotation.items && quotation.items.length > 0) {
        for (var i = 0; i < quotation.items.length; i++) {
          var item = quotation.items[i];
          total += (item.price || 0) * (item.quantity || 0);
        }
      }
      return total;
    },

    matchesFilters(item) {
      const f = this.filters;
      if (f.orderNo && !(item.orderNo || "").toLowerCase().includes(f.orderNo.toLowerCase())) return false;
      if (f.supplierName && !(item.supplierName || "").toLowerCase().includes(f.supplierName.toLowerCase())) return false;
      const d = asDateString(item.date);
      if (f.dateFrom && d && d < f.dateFrom) return false;
      if (f.dateTo && this.dateRangeValid && d && d > f.dateTo) return false;
      return true;
    },

    // Load org-wide vendor quotations for real analytics (KPIs, trend)
    fetchSummary() {
      this.api
        .getListByCurrentBusiness()
        .then((r) => {
          const list = (r && ((r.result && (r.result.data || r.result)) || r.data)) || [];
          this.allPurchaseQuotations = Array.isArray(list) ? list : [];
        })
        .catch(() => { /* analytics degrade gracefully; the table still works */ });
    },

    formatCurrency(amount) {
      return `RM ${formatMyr(amount)}`;
    },

    refreshAll() {
      this.refreshTable();
      this.fetchSummary();
    },

    refreshTable() {
      this.loading = true;
      this.loadError = false;
      this.api
        .getListByCurrentBusiness()
        .then((response) => {
          const list = response.result || [];
          this.items = Array.isArray(list) ? list : [];
        })
        .catch((err) => {
          console.error("Failed to load vendor quotations", err);
          this.loadError = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },

    onEdit(item) {
      this.$router.push({
        path: `/tenants/PurchaseQuotation/${item.id}`,
      });
    },

    showDeleteConfirmation(item) {
      this.itemToDelete = item;
      this.warningModal = true;
    },

    confirmDelete() {
      this.deleting = true;
      const no = this.itemToDelete.orderNo || "";
      this.api
        .delete(this.itemToDelete.id)
        .then(() => {
          this.$toast.success(`Vendor quotation ${no} deleted`);
          this.warningModal = false;
          this.refreshAll();
        })
        .catch(({ data } = {}) => {
          this.$toast.error(
            `Couldn't delete vendor quotation ${no}. ${helper.getErrorMessage(data, "Please try again.")}`
          );
        })
        .finally(() => {
          this.deleting = false;
        });
    },

    onDeleteConfirmation(status) {
      if (!status && !this.deleting) {
        this.itemToDelete = {};
      }
    },

    addNew() {
      this.$router.push({ path: "/tenants/PurchaseQuotation" });
    },

    resetFilters() {
      this.filters = {
        orderNo: "",
        supplierName: "",
        dateFrom: "",
        dateTo: "",
      };
    },
  },
};
</script>

<style scoped>
.spinner-border {
  width: 2rem;
  height: 2rem;
}

/* ============================================================= */
/* Brand — navy #1E3A5F / emerald #10B981 (same as the other list screens) */
/* ============================================================= */
h1 { color: #1e3a5f; }
::v-deep .btn-primary {
  background-color: #10b981;
  border-color: #10b981;
}
::v-deep .btn-primary:hover,
::v-deep .btn-primary:focus,
::v-deep .btn-primary:not(:disabled):not(.disabled):active {
  background-color: #059669;
  border-color: #059669;
}

/* KPI tiles */
.inv-kpi {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 1.1rem 1.15rem;
  height: 100%;
}
.inv-kpi-top { display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.75rem; }
.inv-kpi-label { font-size: 0.8rem; font-weight: 600; color: #64748b; }
.inv-kpi-chip { width: 34px; height: 34px; border-radius: 9px; display: grid; place-items: center; font-size: 0.95rem; }
.chip-navy { background: rgba(30, 58, 95, 0.1); color: #1e3a5f; }
.chip-success { background: rgba(16, 185, 129, 0.13); color: #059669; }
.inv-kpi-value { font-size: 1.5rem; font-weight: 800; color: #1e3a5f; line-height: 1.1; }
.inv-kpi-sub { font-size: 0.74rem; color: #64748b; margin-top: 0.35rem; }
.inv-kpi-sub.trend-up { color: #059669; }
.inv-kpi-sub.trend-down { color: #ef4444; }

::v-deep .table thead th {
  font-size: 0.8rem;
  color: #64748b;
  border-top: none;
  border-bottom: 1px solid #e2e8f0;
}
::v-deep .table tbody td { vertical-align: middle; }
</style>
